import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import type { ServiceAuditResponse, SCCableRecommendation } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

const NEC_CONTINUOUS_DERATE_PCT = 80;
const BREAKER_TRIP_PCT = 100;

function fmtAmps(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(0)}A`;
}
function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(0)}%`;
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
function dataStateLabel(s: ServiceAuditResponse["roster"]["data_state"]): string {
  switch (s) {
    case "ok": return "ok";
    case "moderate_confidence": return "moderate";
    case "low_confidence": return "low";
    case "pending": return "pending";
    case "no_data": return "no data";
    default: return "missing";
  }
}
function dataStateTone(s: ServiceAuditResponse["roster"]["data_state"]): string {
  switch (s) {
    case "ok": return "text-emerald-300";
    case "moderate_confidence": return "text-amber-300";
    case "low_confidence":
    case "pending": return "text-amber-400";
    case "no_data": return "text-red-400";
    default: return "text-muted-foreground";
  }
}

function headroomTone(pct: number | null | undefined, threshold: number): string {
  if (pct == null) return "text-muted-foreground";
  if (pct >= threshold) return "text-red-400";
  if (pct >= threshold * 0.95) return "text-amber-400";
  return "text-emerald-400";
}

export function ServiceAudit() {
  const { systemId = "" } = useParams();
  const [sp] = useSearchParams();
  const windowId = sp.get("window_id") ?? sp.get("window") ?? "";

  const [data, setData] = useState<ServiceAuditResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!systemId || !windowId) return;
    setLoading(true);
    setError(null);
    api.get<ServiceAuditResponse>(
      `/api/services/${encodeURIComponent(systemId)}/audit?window_id=${encodeURIComponent(windowId)}`,
    )
      .then((d) => setData(d))
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : String(e));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [systemId, windowId]);

  useEffect(() => { load(); }, [load]);

  if (!systemId || !windowId) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Missing system_id or window_id.{" "}
        <Link to="/admin/load-in" className="underline">Pick a service from Load-in</Link>.
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 space-y-4 max-w-6xl mx-auto">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Service Audit</h1>
          <div className="text-sm text-muted-foreground">
            <span className="font-mono">{systemId}</span>
            {data?.service_name && <span> · {data.service_name}</span>}
            {data?.event_name && <span> · {data.event_name}</span>}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {error && (
        <Card>
          <CardContent className="py-4 text-sm text-red-400">{error}</CardContent>
        </Card>
      )}

      {!data && !error && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {loading ? "Loading service audit…" : "No data."}
          </CardContent>
        </Card>
      )}

      {data && <AuditBody data={data} />}
    </div>
  );
}

function AuditBody({ data }: { data: ServiceAuditResponse }) {
  const unavailable = data.source === "unavailable";
  return (
    <>
      <IdentityCard data={data} />
      <SizingCoreCard data={data} />
      <PerLegCard data={data} />
      <CableRecommendationCard rec={data.sc_cable_recommendation} />
      {unavailable ? (
        <Card>
          <CardContent className="py-4 text-sm text-amber-300">
            No completed show period telemetry yet for this event — the
            sizing numbers above derive from the materialized roster only.
            The chart, threshold context, and AC-input columns become
            available after the event's first show period ends.
          </CardContent>
        </Card>
      ) : (
        <>
          <TimeSeriesCard data={data} />
          <ContextStripCard data={data} />
        </>
      )}
      <LifetimeCard data={data} />
    </>
  );
}

function IdentityCard({ data }: { data: ServiceAuditResponse }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Identity</CardTitle>
      </CardHeader>
      <CardContent className="text-sm grid grid-cols-2 md:grid-cols-3 gap-3">
        <Pair label="Service" value={data.service_name ?? "—"} />
        <Pair label="Breaker"
              value={data.service_capacity_amps != null
                ? `${data.service_capacity_amps} A`
                : "—"} />
        <Pair label="Location" value={data.service_location_label ?? "—"} />
        <Pair label="Touring identity"
              value={data.touring_identity
                ? `${data.touring_identity}${data.tier ? ` · ${data.tier}` : ""}`
                : "—"} />
        <Pair label="House feed"
              value={data.house_feed
                ? `${data.house_feed.feed_name} (${data.house_feed.capacity_amps} A)`
                : "untagged"} />
        <Pair label="Meter type" value={data.system_type ?? "—"} />
      </CardContent>
    </Card>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function SizingCoreCard({ data }: { data: ServiceAuditResponse }) {
  const transient = data.worst_leg_transient_a;
  const demand = data.worst_leg_demand_15min_a;
  const continuous = data.worst_leg_continuous_a;
  const r = data.roster;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Sizing core</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <Metric
            label="True-max (lifetime)"
            sublabel={
              r.true_max_provenance
                ? String(
                    (r.true_max_provenance as Record<string, unknown>).source
                    ?? "vm_scan",
                  )
                : "—"
            }
            valueA={r.true_max_a}
          />
          <Metric
            label="Show-condition max"
            sublabel="planning peak across completed periods"
            valueA={r.show_condition_max_a}
          />
          <Metric
            label="Worst-leg transient"
            sublabel="10s peak"
            valueA={transient}
            pct={data.breaker_headroom_pct_at_100}
            tone={headroomTone(data.breaker_headroom_pct_at_100, BREAKER_TRIP_PCT)}
          />
          <Metric
            label="15-min demand"
            sublabel={demand == null ? "no 15-min window" : "rolling max"}
            valueA={demand}
            pct={data.demand_headroom_pct_at_100}
            tone={headroomTone(data.demand_headroom_pct_at_100, BREAKER_TRIP_PCT)}
          />
          <Metric
            label="3h continuous"
            sublabel={
              continuous == null
                ? "no ≥3h sustained run"
                : "NEC 210.20 trigger"
            }
            valueA={continuous}
            pct={data.breaker_headroom_pct_at_80}
            tone={headroomTone(data.breaker_headroom_pct_at_80, NEC_CONTINUOUS_DERATE_PCT)}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Metric
            label="Phase imbalance"
            sublabel="leg avg deviation"
            valueA={null}
            valueOverride={fmtPct(data.phase_imbalance_pct)}
          />
          <Metric
            label="Diversity factor"
            sublabel="worst leg ÷ sum of legs"
            valueA={null}
            valueOverride={
              data.diversity_factor == null
                ? "—"
                : data.diversity_factor.toFixed(2)
            }
          />
          <Metric
            label="Confidence"
            sublabel={
              r.evidence_count != null
                ? `${r.evidence_count} declared show${r.evidence_count === 1 ? "" : "s"} with measured load`
                : "no roster row"
            }
            valueA={null}
            valueOverride={dataStateLabel(r.data_state)}
            tone={dataStateTone(r.data_state)}
          />
          <Metric
            label="Computed"
            sublabel="roster freshness"
            valueA={null}
            valueOverride={fmtDate(r.computed_at)}
          />
        </div>
        <ProvenanceLine provenance={r.true_max_provenance} />
      </CardContent>
    </Card>
  );
}

function ProvenanceLine({
  provenance,
}: {
  provenance: Record<string, unknown> | null;
}) {
  if (!provenance) return null;
  const sustained = Boolean(provenance.sustained);
  const evidence = Number(provenance.evidence_count ?? 0);
  const durationS = Number(provenance.peak_run_duration_s ?? 0);
  const spanDays = Number(provenance.data_span_days ?? 0);
  const source = String(provenance.source ?? "vm_scan");
  const peakA = Number(provenance.peak_magnitude_a ?? 0);

  const durationLabel = durationS >= 3600
    ? `${(durationS / 3600).toFixed(1)} h`
    : `${Math.round(durationS / 60)} min`;

  return (
    <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 pt-1 border-t border-border/40">
      <span>
        Sustained:{" "}
        <span className={sustained ? "text-emerald-300" : "text-amber-300"}>
          {sustained ? "yes" : "no"}
        </span>
      </span>
      <span>
        Shows with load: <span className="text-foreground">{evidence}</span>
      </span>
      <span>Best run: {durationLabel} @ {peakA.toFixed(0)} A</span>
      <span>Data span: {spanDays.toFixed(1)} d</span>
      <span>Source: {source}</span>
    </div>
  );
}

function PerLegCard({ data }: { data: ServiceAuditResponse }) {
  const breaker = data.service_capacity_amps;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Per-leg breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 text-sm">
          {(["l1", "l2", "l3"] as const).map((leg) => {
            const s = data.per_leg[leg];
            const transPct =
              breaker && breaker > 0 ? (s.transient_a / breaker) * 100 : null;
            const demandPct =
              s.demand_15min_a != null && breaker && breaker > 0
                ? (s.demand_15min_a / breaker) * 100
                : null;
            const contPct =
              s.continuous_a != null && breaker && breaker > 0
                ? (s.continuous_a / (breaker * (NEC_CONTINUOUS_DERATE_PCT / 100))) * 100
                : null;
            return (
              <div key={leg} className="rounded border border-border/60 p-2 space-y-0.5">
                <div className="text-xs font-mono text-muted-foreground">
                  Leg {leg.toUpperCase()}
                </div>
                <div className={cn("text-sm", headroomTone(transPct, BREAKER_TRIP_PCT))}>
                  {fmtAmps(s.transient_a)} transient
                  {transPct != null && (
                    <span className="text-xs ml-1 text-muted-foreground">
                      ({fmtPct(transPct)})
                    </span>
                  )}
                </div>
                <div className={cn("text-sm", headroomTone(demandPct, BREAKER_TRIP_PCT))}>
                  {s.demand_15min_a == null
                    ? "no 15-min window"
                    : `${fmtAmps(s.demand_15min_a)} demand`}
                  {demandPct != null && (
                    <span className="text-xs ml-1 text-muted-foreground">
                      ({fmtPct(demandPct)})
                    </span>
                  )}
                </div>
                <div className={cn("text-sm", headroomTone(contPct, NEC_CONTINUOUS_DERATE_PCT))}>
                  {s.continuous_a == null
                    ? "no sustained run"
                    : `${fmtAmps(s.continuous_a)} continuous`}
                  {contPct != null && (
                    <span className="text-xs ml-1 text-muted-foreground">
                      ({fmtPct(contPct)})
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  avg {fmtAmps(s.avg_a)}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function CableRecommendationCard({ rec }: { rec: SCCableRecommendation | null }) {
  if (!rec) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recommended cable</CardTitle>
        </CardHeader>
        <CardContent className="py-2 text-sm text-muted-foreground">
          No load detected — no recommendation available.
        </CardContent>
      </Card>
    );
  }
  const sizeLabel = rec.parallel > 1
    ? `${rec.parallel}× 4/0 parallel`
    : `${rec.awg} AWG`;
  const tone = rec.limiter === "over_max"
    ? "text-amber-300"
    : rec.awg === "4/0" || rec.awg === "2/0"
      ? "text-muted-foreground"
      : "text-emerald-300";
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Recommended cable (Type SC, NEC 400-6(B))</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className={cn("text-2xl font-semibold", tone)}>{sizeLabel}</span>
          <span className="text-sm text-muted-foreground">
            {rec.parallel * rec.ampacity_a} A ampacity · needs ≥{rec.needed_a.toFixed(0)} A ({rec.limiter} gate)
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          Smallest camlock-standard size that satisfies this service's observed
          peak + NEC 80% continuous derate.
        </div>
      </CardContent>
    </Card>
  );
}

function TimeSeriesCard({ data }: { data: ServiceAuditResponse }) {
  // SVG sparkline-style chart, no extra deps. uPlot is available in this
  // repo but used elsewhere for live ticks; for a static post-show chart
  // an SVG keeps this page lightweight.
  const series = data.time_series;
  if (!series.length) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Per-leg current — chart period</CardTitle>
        </CardHeader>
        <CardContent className="py-2 text-sm text-muted-foreground">
          No telemetry available for the chart period.
        </CardContent>
      </Card>
    );
  }
  const tsValues = series.map((r) => r[0]);
  const t0 = tsValues[0];
  const tN = tsValues[tsValues.length - 1];
  const breaker = data.service_capacity_amps ?? 0;
  const maxLegVal = Math.max(
    ...series.flatMap((r) => [r[1] ?? 0, r[2] ?? 0, r[3] ?? 0]),
    breaker || 0,
  );
  const W = 800, H = 200, PADL = 36, PADR = 8, PADT = 8, PADB = 22;
  const innerW = W - PADL - PADR;
  const innerH = H - PADT - PADB;
  const x = (ts: number) => PADL + ((ts - t0) / Math.max(1, tN - t0)) * innerW;
  const y = (v: number) => PADT + innerH - (v / Math.max(1, maxLegVal)) * innerH;

  const path = (idx: 1 | 2 | 3) => {
    const pts: string[] = [];
    let pen = false;
    for (const r of series) {
      const v = r[idx];
      if (v == null) { pen = false; continue; }
      pts.push(`${pen ? "L" : "M"}${x(r[0]).toFixed(1)},${y(v).toFixed(1)}`);
      pen = true;
    }
    return pts.join(" ");
  };

  const breakerY = breaker > 0 ? y(breaker) : null;
  const contY = breaker > 0 ? y(breaker * 0.8) : null;

  const chartPeriod = data.periods_included.find((p) => p.is_chart_period);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Per-leg current — chart period</CardTitle>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <rect x={0} y={0} width={W} height={H} fill="transparent" />
          {breakerY != null && (
            <>
              <line x1={PADL} y1={breakerY} x2={W - PADR} y2={breakerY}
                stroke="rgb(239 68 68 / 0.6)" strokeDasharray="4,3" />
              <text x={W - PADR - 2} y={breakerY - 2} textAnchor="end"
                fontSize="9" fill="rgb(239 68 68 / 0.8)">breaker {breaker}A</text>
            </>
          )}
          {contY != null && (
            <>
              <line x1={PADL} y1={contY} x2={W - PADR} y2={contY}
                stroke="rgb(245 158 11 / 0.5)" strokeDasharray="2,3" />
              <text x={W - PADR - 2} y={contY - 2} textAnchor="end"
                fontSize="9" fill="rgb(245 158 11 / 0.8)">NEC 80%</text>
            </>
          )}
          <path d={path(1)} fill="none" stroke="rgb(96 165 250)" strokeWidth="1.2" />
          <path d={path(2)} fill="none" stroke="rgb(52 211 153)" strokeWidth="1.2" />
          <path d={path(3)} fill="none" stroke="rgb(244 114 182)" strokeWidth="1.2" />
          <line x1={PADL} y1={PADT + innerH} x2={W - PADR} y2={PADT + innerH}
            stroke="rgb(100 116 139 / 0.4)" />
          <text x={PADL} y={H - 4} fontSize="9" fill="rgb(148 163 184)">
            {new Date(t0 / 1e6).toLocaleString()}
          </text>
          <text x={W - PADR} y={H - 4} fontSize="9" fill="rgb(148 163 184)" textAnchor="end">
            {new Date(tN / 1e6).toLocaleString()}
          </text>
          <text x={4} y={PADT + 8} fontSize="9" fill="rgb(148 163 184)">{maxLegVal.toFixed(0)}A</text>
          <text x={4} y={PADT + innerH} fontSize="9" fill="rgb(148 163 184)">0</text>
        </svg>
        <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
          <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: "rgb(96 165 250)" }} /> L1</span>
          <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: "rgb(52 211 153)" }} /> L2</span>
          <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: "rgb(244 114 182)" }} /> L3</span>
          {chartPeriod && (
            <span className="ml-auto">
              chart period · peak {fmtAmps(chartPeriod.peak_a)}
              {data.periods_included.length > 1 &&
                ` (1 of ${data.periods_included.length} periods)`}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ContextStripCard({ data }: { data: ServiceAuditResponse }) {
  return (
    <Card>
      <CardContent className="py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Pair label="AC input at peak"
              value={data.ac_input_at_peak ?? "unknown"} />
        <Pair label="Data presence"
              value={data.data_presence.pct != null
                ? `${data.data_presence.pct.toFixed(0)}%`
                : "—"} />
        <Pair label="Source"
              value={data.source === "unavailable" ? "unavailable" : "cloud-vm 10s_max"} />
        <Pair label="Periods included"
              value={`${data.periods_included.length}`} />
      </CardContent>
    </Card>
  );
}

function LifetimeCard({ data }: { data: ServiceAuditResponse }) {
  const entries = data.lifetime.entries;
  if (entries.length === 0) {
    return null;
  }
  // Tiny inline bar sparkline of show_condition_max_a per event (oldest →
  // newest left-to-right).
  const ordered = [...entries].reverse();
  const maxV = Math.max(
    ...ordered.map((e) => e.show_condition_max_a ?? e.true_max_a ?? 0),
    1,
  );
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Lifetime context</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-3 text-sm">
          <span className="text-muted-foreground">Past events:</span>
          <span>{data.lifetime.event_count}</span>
          <span className="text-muted-foreground">Lifetime peak:</span>
          <span>{fmtAmps(data.lifetime.peak_a)}</span>
          <span className="text-muted-foreground">at:</span>
          <span>{fmtDate(data.lifetime.peak_at)}</span>
        </div>
        <div className="flex items-end gap-0.5 h-12">
          {ordered.map((e) => {
            const v = e.show_condition_max_a ?? e.true_max_a ?? 0;
            const h = Math.max(2, (v / maxV) * 48);
            return (
              <div
                key={e.membership_id}
                className="flex-1 bg-emerald-500/40 rounded-sm"
                style={{ height: `${h}px` }}
                title={`${e.event_name ?? "—"} · ${fmtAmps(v)}`}
              />
            );
          })}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Started</TableHead>
              <TableHead className="text-right">Show-max</TableHead>
              <TableHead className="text-right">True-max</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.membership_id}>
                <TableCell>{e.event_name ?? "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {fmtDate(e.started_at)}
                </TableCell>
                <TableCell className="text-right">{fmtAmps(e.show_condition_max_a)}</TableCell>
                <TableCell className="text-right">{fmtAmps(e.true_max_a)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.data_state ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function Metric({
  label, sublabel, valueA, pct, tone, valueOverride,
}: {
  label: string;
  sublabel: string;
  valueA: number | null;
  pct?: number | null;
  tone?: string;
  valueOverride?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-lg font-semibold", tone ?? "")}>
        {valueOverride ?? (valueA == null ? "—" : fmtAmps(valueA))}
        {pct != null && (
          <span className="text-xs ml-2 align-middle text-muted-foreground">
            {fmtPct(pct)}
          </span>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground">{sublabel}</div>
    </div>
  );
}
